/**
 * AuctionNotificationSystem
 *
 * Mounted once in App.tsx. Receives real-time notifications via SSE
 * (Server-Sent Events) — no polling loop, zero background traffic.
 *
 * When the SSE endpoint is not yet live, the hook transparently falls back to
 * "smart polling" that only fires when the user returns to the tab.
 *
 * Notification types handled:
 *  Viewer  → auction_won / auction_win  → AuctionWinnerModal + fanfare sound
 *  Creator → new_earning / date_completed / payout_approved / tokens_credited
 *                                        → CreatorPaymentModal + money sound
 *
 * Deduplication: shown notification IDs persisted in localStorage so the modal
 * never fires twice for the same event, even across page refreshes.
 */

import React, { useCallback, useRef, useState } from 'react';
import { useAppSelector } from '../hooks/redux';
import { useSSENotifications, SSENotification } from '../hooks/useSSENotifications';
import AuctionWinnerModal from './modals/AuctionWinnerModal';
import { formatGameARamaText } from '../utils/auctionLabels';
import CreatorPaymentModal, { PaymentSource } from './modals/CreatorPaymentModal';
import {
  getShownNotificationIds,
  markNotificationShown,
  MODAL_NOTIFICATION_GUARD_KEY,
  normalizeNotificationId,
} from '../utils/notificationDisplayGuard';

// ─── Notification type sets ───────────────────────────────────────────────────

const WINNER_TYPES = new Set(['auction_won', 'auction_win']);

const PAYMENT_TYPES = new Set([
  'earning_released',   // physical delivery confirmed OR dating code verified OR digital (immediate)
  'new_earning',
  'date_completed',
  'payout_approved',
  'tokens_credited',
  'delivery_completed',
  'auction_sold',
]);

// ─── Helper: map notification type → PaymentSource ───────────────────────────

function resolvePaymentSource(
  type: string,
  data?: SSENotification['data'],
): PaymentSource {
  // delivery_type is set by the backend on all auction earning notifications
  const deliveryType = data?.delivery_type as string | undefined;
  if (deliveryType === 'digital') return 'digital';
  if (deliveryType === 'physical') return 'physical';
  if (deliveryType === 'dating') return 'dating';

  // Fallback checks for older notification shapes
  const src = data?.source as string | undefined;
  if (src === 'digital') return 'digital';
  if (src === 'physical') return 'physical';
  if (src === 'dating' || type === 'date_completed') return 'dating';
  if (src === 'tip' || type === 'new_tip') return 'tip';
  if (src === 'subscription' || type === 'new_subscriber') return 'subscription';
  if (type === 'payout_approved') return 'payout';
  if (type === 'auction_sold' || (data?.sourceType as string) === 'auction') return 'auction';
  return 'general';
}

// ─── Component ────────────────────────────────────────────────────────────────

const AuctionNotificationSystem: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);

  // Winner modal
  const [winnerOpen, setWinnerOpen] = useState(false);
  const [winnerData, setWinnerData] = useState<{
    auctionId?: number;
    auctionTitle?: string;
    winningBid?: number;
    auctionType?: string;
    gameType?: string;
    purchaseType?: 'buy_now' | 'bid_winner';
  }>({});

  // Payment modal
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentData, setPaymentData] = useState<{
    amount?: number;
    source?: PaymentSource;
    title?: string;
    message?: string;
  }>({});

  // Queue — ensures multiple notifications don't overlap
  type QueueItem = { kind: 'winner' | 'payment'; notif: SSENotification };
  const queueRef = useRef<QueueItem[]>([]);
  const busyRef = useRef(false);

  const processQueue = useCallback(() => {
    if (busyRef.current || queueRef.current.length === 0) return;
    busyRef.current = true;

    const next = queueRef.current.shift()!;

    if (next.kind === 'winner') {
      const d = next.notif.data ?? {};
      setWinnerData({
        auctionId: (d.auction_id ?? d.auctionId) as number | undefined,
        auctionTitle: formatGameARamaText(next.notif.title),
        winningBid: (d.winning_bid ?? d.winningBid) as number | undefined,
        auctionType: (d.auction_type ?? d.auctionType) as string | undefined,
        gameType: (d.auction_type ?? d.auctionType) as string | undefined,
        purchaseType: (d.purchase_type ?? d.purchaseType) as 'buy_now' | 'bid_winner' | undefined,
      });
      setWinnerOpen(true);
    } else {
      const d = next.notif.data ?? {};
      setPaymentData({
        amount: d.amount as number | undefined,
        source: resolvePaymentSource(next.notif.type, d),
        title: formatGameARamaText(next.notif.title),
        message: formatGameARamaText(next.notif.message),
      });
      setPaymentOpen(true);
    }
  }, []);

  const handleWinnerClose = useCallback(() => {
    setWinnerOpen(false);
    busyRef.current = false;
    setTimeout(processQueue, 500);
  }, [processQueue]);

  const handlePaymentClose = useCallback(() => {
    setPaymentOpen(false);
    busyRef.current = false;
    setTimeout(processQueue, 500);
  }, [processQueue]);

  // ─── SSE handler — called by the hook whenever new notifications arrive ────

  const handleNotifications = useCallback(
    (notifications: SSENotification[]) => {
      window.dispatchEvent(new CustomEvent('issac:notifications', { detail: notifications }));

      const shown = getShownNotificationIds(MODAL_NOTIFICATION_GUARD_KEY);
      const toQueue: QueueItem[] = [];

      for (const notif of notifications) {
        const id = normalizeNotificationId(notif.id);
        if (!id || shown.has(id)) continue;
        markNotificationShown(id, MODAL_NOTIFICATION_GUARD_KEY);
        shown.add(id);

        if (WINNER_TYPES.has(notif.type)) {
          toQueue.push({ kind: 'winner', notif });
        } else if (PAYMENT_TYPES.has(notif.type) && user?.role === 'model') {
          toQueue.push({ kind: 'payment', notif });
        }
      }

      if (toQueue.length > 0) {
        queueRef.current.push(...toQueue);
        processQueue();
      }
    },
    [user?.role, processQueue],
  );

  // ─── SSE connection (replaces all polling) ────────────────────────────────

  useSSENotifications({ onNotifications: handleNotifications });

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <AuctionWinnerModal
        isOpen={winnerOpen}
        onClose={handleWinnerClose}
        auctionId={winnerData.auctionId}
        auctionTitle={winnerData.auctionTitle}
        winningBid={winnerData.winningBid}
        auctionType={winnerData.auctionType}
        gameType={winnerData.gameType}
        purchaseType={winnerData.purchaseType}
      />
      <CreatorPaymentModal
        isOpen={paymentOpen}
        onClose={handlePaymentClose}
        amount={paymentData.amount}
        source={paymentData.source}
        title={paymentData.title}
        message={paymentData.message}
      />
    </>
  );
};

export default AuctionNotificationSystem;
